/**
 * <TutorHistoryOccurrenceRow> — une séance passée du masteur, une ligne de son historique
 * (organism).
 *
 * L'ancienne table `LivesHistory` (colonnes Date · Description · Statut · Montant), en ligne de
 * carte-liste : la date en gras, la description, puis le statut et ce qui a été payé. Le statut
 * est celui de l'ancienne colonne : « En attente » (à valider, ou validée sans virement),
 * « À faire » / « Non fait » (selon que la date est passée — l'adaptateur le décide), « Validé ».
 * Le virement vers le solde Stripe s'écrit dessous : « Transféré sur mon solde Stripe le … ».
 *
 * Une séance qui a des comptes-rendus se DÉPLIE (le triangle de l'ancienne date) : un bloc par
 * élève (`<TutorHistoryReport>`). Déplié ou non est un état de vue : la ligne le garde.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { type BadgeVariant } from "../../atoms/Badge/badgeFace"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { CardListRow } from "../../molecules/CardList"
import { formatDayTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    type UITutorHistoryOccurrence,
    type UITutorHistoryReport,
    type UITutorHistoryStatus,
} from "../../../ports/primary/ui/types/tutorLives"
import { TutorHistoryReport } from "./TutorHistoryReport"

const STATUS: Record<UITutorHistoryStatus, { label: string; variant: BadgeVariant; tinted?: boolean }> = {
    pending: { label: "En attente", variant: "warning" },
    todo: { label: "À faire", variant: "info", tinted: true },
    missed: { label: "Non fait", variant: "neutral" },
    validated: { label: "Validé", variant: "success", tinted: true },
}

export type TutorHistoryOccurrenceRowProps = {
    occurrence: UITutorHistoryOccurrence
    /** Déplié au montage — une séance qu'on vient d'ouvrir, ou Storybook. Défaut `false`. */
    defaultExpanded?: boolean
    onOpenStudent?: (report: UITutorHistoryReport) => void
    onOpenReport?: (report: UITutorHistoryReport) => void
    testID?: string
}

export function TutorHistoryOccurrenceRow(props: TutorHistoryOccurrenceRowProps) {
    const { occurrence, defaultExpanded = false, onOpenStudent, onOpenReport, testID } = props
    const [expanded, setExpanded] = React.useState(defaultExpanded)
    const expandable = occurrence.reports.length > 0
    const status = STATUS[occurrence.status]

    const summary = (
        <Row gap="3" align="start">
            <Stack gap="1" style={styles.body}>
                <Text size="text-md" weight="bold" color={expandable ? "text-primary" : "text-muted"}>
                    {formatDayTime(occurrence.date)}
                </Text>
                <Text size="text-sm" color="text-muted" numberOfLines={2}>
                    {occurrence.description ?? "-"}
                </Text>
                <Row gap="2" align="center" style={styles.wrap}>
                    <Badge text={status.label} variant={status.variant} tinted={status.tinted} />
                    <Text size="text-sm" weight="bold" color="text-primary">
                        {occurrence.amountLabel ?? "-"}
                    </Text>
                </Row>
                {occurrence.transferredAt != null && (
                    <Text size="text-xs" color="text-muted">
                        {`Transféré sur mon solde Stripe le ${formatDayTime(occurrence.transferredAt, false, "sentence")}`}
                    </Text>
                )}
            </Stack>
            {expandable && <Icon name={expanded ? "chevron-up" : "chevron-down"} size={20} color="text-muted" />}
        </Row>
    )

    return (
        <CardListRow testID={testID}>
            {expandable ? (
                <Pressable
                    onPress={() => setExpanded((open) => !open)}
                    accessibilityRole="button"
                    accessibilityState={{ expanded }}
                >
                    {summary}
                </Pressable>
            ) : (
                summary
            )}
            {expandable && expanded && (
                <View style={styles.reports}>
                    {occurrence.reports.map((report) => (
                        <TutorHistoryReport
                            key={report.id}
                            report={report}
                            onOpenStudent={onOpenStudent != null ? () => onOpenStudent(report) : undefined}
                            onOpenReport={onOpenReport != null ? () => onOpenReport(report) : undefined}
                        />
                    ))}
                </View>
            )}
        </CardListRow>
    )
}

const styles = StyleSheet.create((theme) => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
    wrap: {
        flexWrap: "wrap",
    },
    reports: {
        marginTop: theme.spacing["4"],
        gap: theme.spacing["3"],
    },
}))
